/**
 * Cierre de sesión por inactividad + tope absoluto, por rol (frontend-only).
 *
 * Modelo de deadline único (`computeSessionDeadline`, función pura):
 *   deadline = min(idleDeadline, absoluteDeadline), con su MOTIVO
 *   - idleDeadline     = última actividad + policy.idleMs      → 'idle'
 *   - absoluteDeadline = login real (last_sign_in_at) + policy.maxMs → 'absolute'
 *   remaining = deadline - now
 *     · remaining <= 0          → signOut + redirect
 *     · remaining <= warnMs     → mostrar aviso del motivo, con cuenta regresiva
 *     · else                    → ocultar aviso
 *
 * "Mantener sesión" resetea SOLO la actividad (idle), nunca el tope absoluto:
 * por eso el aviso por duración máxima no lo ofrece (antes lo ofrecía y el aviso
 * reaparecía en el siguiente tick, en bucle).
 * Durante el aviso, la actividad del usuario NO extiende la sesión: solo el
 * clic explícito en "Mantener sesión".
 *
 * Sin sesión (anónimo) no corre ningún timer ni listener.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { getCurrentAuthUser } from '@/services/auth.service';
import { getSessionPolicy } from '@/lib/sessionPolicy';
import {
  acceptSharedActivity,
  computeSessionDeadline,
  type SessionDeadline,
  type SessionDeadlineKind,
} from '../lib/sessionDeadline';
import {
  flushActiveConsultation,
  isActiveConsultationDirty,
  markForcedNavigation,
} from '../lib/activeConsultation';

/** Guardado de la consulta activa pedido al entrar en el aviso (PR-4). */
export type ConsultationSaveState = 'none' | 'saving' | 'saved' | 'failed';

const HOUR_MS = 3_600_000;

// Zonas privadas: las que no deben seguir visibles si la sesión se pierde desde
// otra pestaña. En las públicas (home, perfil de médico…) una pérdida de sesión
// no obliga a salir de la página.
const PRIVATE_PATH = /^\/(panel|admin|paciente)(\/|$)/;
function isPrivatePath(pathname: string): boolean {
  const base = (import.meta.env.BASE_URL || '/').replace(/\/$/, '');
  const path = base && pathname.startsWith(base) ? pathname.slice(base.length) || '/' : pathname;
  return PRIVATE_PATH.test(path);
}

const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'mousemove', 'wheel', 'scroll', 'touchstart'];

// ─── Persistencia del reloj de inactividad ─────────────────────────
// Clave que guarda SOLO el timestamp (epoch ms) de la última actividad —
// sin PII (ni nombre/email/teléfono/datos clínicos). Sirve para que el
// timeout por inactividad SOBREVIVA a reload / cierre-reapertura / kill de
// pestaña en móvil (antes el reloj vivía solo en memoria y se reiniciaba en
// cada montaje → el idle no atrapaba una sesión reabierta al día siguiente).
const ACTIVITY_KEY = 'lc_last_activity';
// El write a localStorage se throttlea (la actividad en memoria es exacta;
// solo se persiste como mucho cada N ms para no spamear en mousemove/scroll).
const PERSIST_THROTTLE_MS = 15_000;

function readPersistedActivity(): number | null {
  try {
    const raw = localStorage.getItem(ACTIVITY_KEY);
    if (!raw) return null;
    const n = Number(raw);
    if (!Number.isFinite(n) || n <= 0) return null;
    // Nunca en el futuro (clock skew / manipulación) → tratar como "ahora"
    // para no extender la sesión más allá de lo real.
    return Math.min(n, Date.now());
  } catch {
    return null;
  }
}

// Monótono: la clave es compartida por todas las pestañas de la sesión, así que
// nunca se retrocede a un valor más viejo que el que ya dejó otra pestaña (p. ej.
// el flush de una pestaña que pasa a segundo plano con actividad antigua).
function persistActivity(ts: number): void {
  try {
    const stored = readPersistedActivity();
    if (stored !== null && ts <= stored) return;
    localStorage.setItem(ACTIVITY_KEY, String(ts));
  } catch {
    /* best-effort: modo privado / storage lleno */
  }
}

function clearPersistedActivity(): void {
  try {
    localStorage.removeItem(ACTIVITY_KEY);
  } catch {
    /* best-effort */
  }
}

export interface IdleLogoutState {
  warning: boolean;
  /** Motivo del aviso visible (null = sin aviso). */
  warningKind: SessionDeadlineKind | null;
  secondsLeft: number;
  /** Duración máxima de la sesión del rol, en horas (para el copy del aviso). */
  maxHours: number;
  keepAlive: () => void;
  logoutNow: () => void;
  /** Estado del guardado de la consulta activa durante el aviso. */
  consultationSave: ConsultationSaveState;
  /** Reintento MANUAL tras un fallo (nunca automático). */
  retryConsultationSave: () => void;
}

export function useIdleLogout(): IdleLogoutState {
  const [role, setRole] = useState<string | null>(null);
  const [warningKind, setWarningKind] = useState<SessionDeadlineKind | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [consultationSave, setConsultationSave] = useState<ConsultationSaveState>('none');
  const warning = warningKind !== null;

  const lastActivityRef = useRef<number>(Date.now());
  const lastPersistRef = useRef<number>(0);
  const loginAtRef = useRef<number | null>(null);
  const warningRef = useRef<SessionDeadlineKind | null>(null);
  const loggingOutRef = useRef(false);
  // ¿Esta pestaña tiene una sesión autenticada? (lo lee el handler de auth,
  // que se registra una sola vez y no ve el `role` actualizado).
  const roleRef = useRef<string | null>(null);

  useEffect(() => {
    warningRef.current = warningKind;
  }, [warningKind]);

  useEffect(() => {
    roleRef.current = role;
  }, [role]);

  /**
   * La sesión de ESTA pestaña se perdió desde otra (cierre manual, vencimiento o
   * `SIGNED_OUT` propagado por auth-js). Si la pestaña tenía sesión y está en una
   * zona privada, sale con navegación dura a la raíz: descarta la UI privada y
   * la caché en memoria. No llama a `signOut()` (la sesión ya no está) ni toca
   * los tokens. Idempotente: varias señales casi simultáneas → una sola salida.
   */
  const exitAfterSessionLoss = useCallback((): boolean => {
    if (loggingOutRef.current) return true;
    if (roleRef.current === null) return false;
    if (!isPrivatePath(window.location.pathname)) return false;
    loggingOutRef.current = true;
    setWarningKind(null);
    markForcedNavigation(); // PR-4: el aviso beforeunload de la consulta no la bloquea
    window.location.assign(import.meta.env.BASE_URL || '/');
    return true;
  }, []);

  // ─── Identidad + ancla del tope absoluto ────────────────────
  useEffect(() => {
    let alive = true;

    const refresh = async () => {
      const u = await getCurrentAuthUser().catch(() => null);
      if (!alive) return;
      if (!u) {
        setRole(null);
        loginAtRef.current = null;
        return;
      }
      setRole(u.role);
      // Tope absoluto anclado al login REAL (estable entre reloads y refreshes).
      const { data } = await supabase.auth.getSession();
      const lsi = data.session?.user?.last_sign_in_at;
      loginAtRef.current = lsi ? new Date(lsi).getTime() : Date.now();

      // La actividad nunca puede ser ANTERIOR al login actual: un login nuevo
      // (loginAt = ahora) reinicia el idle; una restauración de sesión en frío
      // conserva la actividad persistida (> loginAt). Robusto e independiente
      // del nombre del evento de auth (no depende de SIGNED_IN vs
      // INITIAL_SESSION), porque se ancla a `last_sign_in_at` —que NO cambia al
      // restaurar ni al refrescar el token—.
      const anchor = loginAtRef.current;
      if (lastActivityRef.current < anchor) {
        lastActivityRef.current = anchor;
        persistActivity(anchor);
        lastPersistRef.current = anchor;
      }
    };

    // Arranque: el reloj de inactividad se HIDRATA del valor persistido (si
    // existe) → así el idle sobrevive a reload / reapertura / kill de pestaña.
    // Sin valor previo (primer uso) → ahora. El clamp `>= loginAt` vive en
    // refresh() (abajo) y reinicia el idle solo ante un login nuevo.
    const persisted = readPersistedActivity();
    lastActivityRef.current = persisted ?? Date.now();
    persistActivity(lastActivityRef.current);
    lastPersistRef.current = lastActivityRef.current;
    refresh();

    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        // Con sesión en una zona privada → salir (antes solo se apagaba el
        // contador y la pantalla quedaba visible sin sesión).
        if (exitAfterSessionLoss()) return;
        setRole(null);
        loginAtRef.current = null;
        setWarningKind(null);
        clearPersistedActivity();
        return;
      }
      // TOKEN_REFRESHED no cambia identidad ni last_sign_in_at.
      // Evita realimentar refresh() desde una renovación de sesión.
      if (event === 'TOKEN_REFRESHED') return;
      // Login NUEVO = actividad ahora, de forma SÍNCRONA. Es imprescindible:
      // refresh() es async (await getSession) y el tick (1 s) podría evaluar
      // ANTES con un `lastActivity` viejo (p.ej. el que escribió el mount) y
      // disparar un falso logout. En auth-js 2.71 `SIGNED_IN` es un login real
      // (la restauración en frío emite `INITIAL_SESSION`, no `SIGNED_IN`), así
      // que resetear acá NO afecta el caso "sobrevivir a la reapertura" — ese
      // lo preserva el clamp `actividad >= loginAt` de refresh().
      if (event === 'SIGNED_IN') {
        const now = Date.now();
        lastActivityRef.current = now;
        persistActivity(now);
        lastPersistRef.current = now;
      }
      refresh();
    });

    return () => {
      alive = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const doLogout = useCallback(() => {
    if (loggingOutRef.current) return;
    loggingOutRef.current = true;
    setWarningKind(null);
    // Revocación server-side best-effort (no bloqueante): el cliente con lock
    // no-op puede colgar el signOut, así que NO dependemos de que resuelva.
    void supabase.auth.signOut({ scope: 'local' }).catch(() => {});
    // Garantía dura de cierre local: limpiar los tokens de Supabase de
    // localStorage (mismo patrón defensivo que main.tsx) y recargar a la raíz.
    // El reload re-bootstrapea sin sesión → estado limpio, sin restos en
    // memoria ni en guards.
    try {
      clearPersistedActivity();
      Object.keys(localStorage).forEach((k) => {
        if (k.startsWith('sb-')) localStorage.removeItem(k);
      });
    } catch {
      /* best-effort */
    }
    // PR-4: cierre de seguridad. No espera ningún guardado (el plazo no se
    // extiende) y el aviso beforeunload de la consulta no lo bloquea.
    markForcedNavigation();
    window.location.assign(import.meta.env.BASE_URL || '/');
  }, []);

  const keepAlive = useCallback(() => {
    // Defensa: el aviso por duración máxima no ofrece este botón, y reiniciar la
    // inactividad no movería ese cierre (el aviso volvería en el siguiente tick).
    if (warningRef.current === 'absolute') return;
    const now = Date.now();
    lastActivityRef.current = now;
    persistActivity(now); // "Mantener sesión" es raro → persistir sin throttle.
    lastPersistRef.current = now;
    setWarningKind(null);
    // Refresca el token para no quedar con uno por expirar; no bloquea.
    supabase.auth.refreshSession().catch(() => {});
  }, []);

  // ─── PR-4: consulta activa durante el aviso ─────────────────
  // Al ENTRAR en el aviso, si la consulta abierta tiene cambios sin guardar, se
  // intenta guardarla UNA vez (la misma ruta estricta de "Guardar borrador").
  // Sin reintentos automáticos: solo el botón del aviso. El plazo no espera.
  const saveRequestedRef = useRef(false);
  // Cada intento tiene un id: un resultado tardío (aviso ya cerrado o intento
  // posterior) se descarta.
  const saveAttemptRef = useRef(0);
  const runConsultationSave = useCallback(() => {
    const attempt = ++saveAttemptRef.current;
    setConsultationSave('saving');
    void flushActiveConsultation().then((r) => {
      if (loggingOutRef.current || attempt !== saveAttemptRef.current) return;
      setConsultationSave(r === 'saved' ? 'saved' : r === 'failed' ? 'failed' : 'none');
    });
  }, []);
  const startConsultationSaveOnWarning = useCallback(() => {
    if (saveRequestedRef.current) return;
    saveRequestedRef.current = true;
    if (isActiveConsultationDirty()) runConsultationSave();
    else setConsultationSave('none');
  }, [runConsultationSave]);
  const retryConsultationSave = useCallback(() => {
    if (warningRef.current === null || loggingOutRef.current) return;
    runConsultationSave();
  }, [runConsultationSave]);
  useEffect(() => {
    if (warningKind === null) {
      saveRequestedRef.current = false;
      saveAttemptRef.current++;
      setConsultationSave('none');
    }
  }, [warningKind]);

  // Plazo más próximo = min(inactividad, tope absoluto), con su motivo.
  // No muta nada; se usa para evaluar Y para el guard de actividad.
  const deadline = useCallback((currentRole: string): SessionDeadline => {
    const now = Date.now();
    return computeSessionDeadline({
      now,
      lastActivity: lastActivityRef.current,
      loginAt: loginAtRef.current ?? now,
      policy: getSessionPolicy(currentRole), // leído en caliente → override de dev
    });
  }, []);

  // Evalúa el estado: si venció → logout; si está por vencer → aviso del motivo;
  // si no → oculta el aviso. Compartido por el tick y por visibilitychange/focus.
  const evaluate = useCallback(
    (currentRole: string) => {
      if (loggingOutRef.current) return;
      const { kind, remainingMs } = deadline(currentRole);
      if (remainingMs <= 0) {
        doLogout();
        return;
      }
      if (remainingMs <= getSessionPolicy(currentRole).warnMs) {
        // Al ENTRAR en aviso, publicar la actividad exacta: el guardado normal se
        // espacia 15 s y otra pestaña podría estar calculando con un valor más
        // viejo (y cerrar antes). Así todas convergen al mismo plazo.
        if (warningRef.current === null) persistActivity(lastActivityRef.current);
        setWarningKind(kind);
        startConsultationSaveOnWarning();
        setSecondsLeft(Math.max(0, Math.ceil(remainingMs / 1000)));
      } else if (warningRef.current) {
        setWarningKind(null);
      }
    },
    [doLogout, deadline, startConsultationSaveOnWarning]
  );

  // ─── Listeners de actividad ─────────────────────────────────
  // Orden defensivo: PRIMERO se evalúa si la sesión ya venció; solo si NO venció
  // se registra actividad. Así un touch/scroll/focus al volver de background NO
  // puede revivir una sesión ya vencida por inactividad o por tope absoluto.
  // Durante el aviso tampoco extiende (solo el botón "Mantener sesión").
  useEffect(() => {
    if (!role) return;
    const onActivity = () => {
      if (warningRef.current) return;       // aviso visible → no extender
      if (deadline(role).remainingMs <= 0) return; // ya venció → NO renovar (el tick cierra)
      const now = Date.now();
      lastActivityRef.current = now;
      // Persistir throttled: la actividad en memoria es exacta; a localStorage
      // solo se escribe como mucho cada PERSIST_THROTTLE_MS (la leve latencia
      // erra hacia un cierre un poco ANTES → conservador/seguro).
      if (now - lastPersistRef.current >= PERSIST_THROTTLE_MS) {
        lastPersistRef.current = now;
        persistActivity(now);
      }
    };
    ACTIVITY_EVENTS.forEach((e) =>
      window.addEventListener(e, onActivity, { passive: true })
    );
    return () => {
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, onActivity));
    };
  }, [role, deadline]);

  // ─── Tick (1 s) + re-evaluación inmediata al volver al primer plano ──
  // En móvil, al reabrir la app (visibilitychange/focus) se evalúa el
  // vencimiento ANTES de cualquier actividad → si ya venció, cierra/avisa.
  useEffect(() => {
    if (!role) {
      setWarningKind(null);
      return;
    }
    const id = setInterval(() => evaluate(role), 1000);
    // Re-evaluar el vencimiento al volver al primer plano.
    const onFocus = () => evaluate(role);
    // Flush del último activity a localStorage al ir a background / descargar
    // la página → el idle-on-reopen es preciso aunque el SO móvil mate la
    // pestaña sin previo aviso (reduce la latencia del throttle a ~0 al cerrar).
    const flush = () => {
      persistActivity(lastActivityRef.current);
      lastPersistRef.current = lastActivityRef.current;
    };
    const onVisChange = () => {
      if (document.visibilityState === 'visible') evaluate(role);
      else flush();
    };
    // Multipestaña: la actividad de OTRA pestaña de la misma sesión llega por el
    // evento nativo `storage` (sin polling). Si es válida y más reciente, esta
    // pestaña la adopta y re-evalúa al momento: así una pestaña inactiva no cierra
    // la sesión mientras se trabaja en otra, y "Mantener sesión" en una retira el
    // aviso de las demás. El tope absoluto no se mueve (sale de loginAt).
    const onStorage = (e: StorageEvent) => {
      if (e.key !== ACTIVITY_KEY || e.storageArea !== localStorage) return;
      // La clave solo se BORRA al cerrar sesión (doLogout, hardLocalSignOut y el
      // handler de SIGNED_OUT): otra pestaña cerró la sesión. Esta señal llega
      // aunque el SIGNED_OUT de auth-js no (su /logout lo aborta la navegación).
      if (e.newValue === null) {
        exitAfterSessionLoss();
        return;
      }
      const ts = acceptSharedActivity(e.newValue, {
        known: lastActivityRef.current,
        now: Date.now(),
        loginAt: loginAtRef.current,
      });
      if (ts === null) return;
      lastActivityRef.current = ts;
      evaluate(role);
    };
    document.addEventListener('visibilitychange', onVisChange);
    window.addEventListener('focus', onFocus);
    window.addEventListener('pagehide', flush);
    window.addEventListener('storage', onStorage);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisChange);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('pagehide', flush);
      window.removeEventListener('storage', onStorage);
    };
  }, [role, evaluate]);

  const maxHours = Math.round(getSessionPolicy(role).maxMs / HOUR_MS);

  return {
    warning,
    warningKind,
    secondsLeft,
    maxHours,
    keepAlive,
    logoutNow: doLogout,
    consultationSave,
    retryConsultationSave,
  };
}
